import { useEffect, useState } from 'react';
import {
  getModeOfOnboardingDashboardDetails,
  onboardingSummary,
} from '../../redux-store/actions/application';
import {
  dashboardOnboardingType,
  modeOfOnboardingDashboardType,
} from '../../redux-store/types/api-types';
import data from './data-ts';
import { useDispatch, useSelector } from 'react-redux';
import CenterTitlePlugin from './center-title-plugin-ts';
import { Chart as ChartJS, ChartData, ChartOptions, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Card, Grid, Skeleton, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { RootStateType } from '../../redux-store/reducers';
import { USER_ROLES } from '../../utils/constant';

ChartJS.register(...registerables);

export const dougnutType: dashboardOnboardingType = [
  {
    status: '',
    count: 0,
  },
];

export const modeOfOnboardingDougnutType: modeOfOnboardingDashboardType = [
  {
    modeOfOnboarding: '',
    count: 0,
  },
];

export default function DoughnutGraph({ type }: { type: string }): JSX.Element {
  const dispatch = useDispatch();
  const isNumberOfApplications = type === 'noa';
  const isModeOfOnboardingDataDougnut = type === 'modeOfOnboarding';
  const doughnutData = useSelector((store: RootStateType) =>
    isNumberOfApplications ? store.dataDougnut : store.modeOfOnboardingDataDougnut
  );
  const [dataDoughnut, SetDataDoughnut] = useState(
    isNumberOfApplications ? dougnutType : modeOfOnboardingDougnutType
  );
  const { role = '' } = useSelector((store: RootStateType) => store.auth);

  useEffect(() => {
    let componentIsAlive = true;
    (async function () {
      try {
        (await dispatch(
          isNumberOfApplications ? onboardingSummary() : getModeOfOnboardingDashboardDetails()
        )) as unknown as dashboardOnboardingType | modeOfOnboardingDashboardType;
        if (!componentIsAlive) {
          return;
        }
      } catch (e) {
        console.error((e as Error).message);
      }
    })();
    return () => {
      componentIsAlive = false;
    };
  }, []);

  useEffect(() => {
    if (isModeOfOnboardingDataDougnut) {
      SetDataDoughnut(doughnutData as modeOfOnboardingDashboardType);
    } else {
      SetDataDoughnut(doughnutData as dashboardOnboardingType);
    }
  }, [doughnutData, isModeOfOnboardingDataDougnut]);

  data.donutOptions.plugins.title.text =
    USER_ROLES.AMC_ADMIN === role
      ? 'Number of Applications (since inception)'
      : 'Number of Applications (onboarded by RM/SRM)';

  let completedApplication = 0;
  let inProgressApplication = 0;

  doughnutData?.map((data) => {
    if (isModeOfOnboardingDataDougnut) {
      const modeData = data as { modeOfOnboarding: string; count: number };
      if (modeData.modeOfOnboarding === 'digital') {
        completedApplication = modeData.count;
      }
      if (modeData.modeOfOnboarding === 'phygital') {
        inProgressApplication = modeData.count;
      }
    } else {
      const statusData = data as { status: string; count: number };
      if (['completed'].includes(statusData.status)) {
        completedApplication = statusData.count;
      }
      if (['in_progress'].includes(statusData.status)) {
        inProgressApplication = statusData.count;
      }
    }
  });

  const applicationData = [completedApplication, inProgressApplication];
  const label = [
    [
      isNumberOfApplications ? `Completed` : isModeOfOnboardingDataDougnut ? `Digital` : ``,
      completedApplication,
    ],
    [
      isNumberOfApplications ? `In Progress` : isModeOfOnboardingDataDougnut ? `Phygital` : ``,
      inProgressApplication,
    ],
  ];

  const baseOptions =
    isNumberOfApplications || isModeOfOnboardingDataDougnut
      ? data.donutOptions2
      : data.donutOptions;

  const baseDonutData =
    isNumberOfApplications || isModeOfOnboardingDataDougnut ? data.donutData2 : data.donutData;

  const options = JSON.parse(JSON.stringify(baseOptions));
  const donutData = JSON.parse(JSON.stringify(baseDonutData));

  (donutData.datasets as any)[0].data = [completedApplication, inProgressApplication];
  options.plugins.centerTitle.display = true;
  options.plugins.centerTitle.text = [
    String(applicationData.reduce<number>((sum, num) => (sum ? sum : 0) + (num ? num : 0), 0)),
    'Total Applications',
  ];
  const donutOptions = options as unknown as ChartOptions;

  (donutData.labels as any) = label;

  return (
    <Grid item xs={12} sm={12} md={12} lg={5}>
      <Card raised={true} sx={{ p: 2, alignItems: 'stretch' }}>
        {(isModeOfOnboardingDataDougnut
          ? JSON.stringify(modeOfOnboardingDougnutType) === JSON.stringify(doughnutData)
          : JSON.stringify(dougnutType) === JSON.stringify(doughnutData)) ||
        JSON.stringify(doughnutData) !== JSON.stringify(dataDoughnut) ? (
          <>
            <Skeleton />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Skeleton
                variant="circular"
                sx={{
                  width: { xs: 150, sm: 170 },
                  height: { xs: 150, sm: 170 },
                  mt: 2,
                  ml: { xs: 0, sm: 15, lg: 2 },
                }}
              />
              <Box>
                <Skeleton sx={{ width: { xs: 150, sm: 200 }, ml: { xs: 4, sm: 0 } }} />
                <Skeleton sx={{ width: { xs: 30, sm: 50 }, ml: { xs: 10, sm: 5 } }} />
                <Skeleton sx={{ width: { xs: 150, sm: 200 }, ml: { xs: 4, sm: 0 } }} />
                <Skeleton sx={{ width: { xs: 30, sm: 50 }, ml: { xs: 10, sm: 5 } }} />
              </Box>
            </Box>
          </>
        ) : (
          <Typography
            component={'div'}
            className="canvas-container"
            sx={{
              '&.canvas-container': {
                position: 'relative',
                width: { xs: '300px', sm: 'unset' },
                margin: 'auto',
              },
            }}>
            <Chart
              height={200}
              type="doughnut"
              plugins={[CenterTitlePlugin]}
              data={donutData as ChartData}
              options={donutOptions}
            />
          </Typography>
        )}
      </Card>
    </Grid>
  );
}
